import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Alert,
  Button,
  IconButton,
  Popover,
  Paper
} from '@mui/material';
import {
  People as PeopleIcon,
  Work as WorkIcon,
  Subscriptions as SubscriptionsIcon,
  PersonAdd as PersonAddIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getDashboardAPI, getDashboardReportsAPI } from '../../lib/api';
import { colors } from '../../utils/colors';
import LoadingProgress from '../../components/common/LoadingProgress';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';

const DashboardOverview = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [costData, setCostData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [calendarView, setCalendarView] = useState('start');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const hasFetchedRef = useRef(false);
  const navigate = useNavigate();
  const chartContainerRef = useRef(null);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchDashboardData();

    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const endOfYear = new Date(now.getFullYear(), 11, 31);

    const startISO = startOfYear.toISOString().split('T')[0];
    const endISO = endOfYear.toISOString().split('T')[0];

    setStartDate(startISO);
    setEndDate(endISO);
    setTempStartDate(startISO);
    setTempEndDate(endISO);

    fetchCostData(startISO, endISO);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getDashboardAPI();
      if (result.status) {
        setDashboardData(result.data);
      } else {
        setError(result.message || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCostData = async (start, end) => {
    try {
      const result = await getDashboardReportsAPI(start, end);
      if (result.status) {
        setCostData(result.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch cost data:', err);
    }
  };

  // Date Range Picker Functions
  const handleDatePickerOpen = (event) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setDatePickerAnchor(event.currentTarget);
    setCalendarView('start');
    setCurrentMonth(new Date().getMonth());
    setCurrentYear(new Date().getFullYear());
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const isFutureDate = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);

  return compareDate > today;
};

  const handleDateSelect = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    if (isFutureDate(date)) return; 
    
    if (calendarView === 'start') {
      setTempStartDate(dateStr);
      if (tempEndDate && new Date(dateStr) > new Date(tempEndDate)) {
        setTempEndDate(dateStr);
      }
      setCalendarView('end');
    } else {
      if (new Date(dateStr) < new Date(tempStartDate)) {
        setTempEndDate(tempStartDate);
        setTempStartDate(dateStr);
      } else {
        setTempEndDate(dateStr);
      }
    }
  };

  const handleApplyDateFilter = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setDatePickerAnchor(null);
    fetchCostData(tempStartDate, tempEndDate);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Select date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calendar rendering functions
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDayOfMonth = getFirstDayOfMonth(currentMonth, currentYear);
    const today = new Date();
    const days = [];

    // Previous month days
    const prevMonthDays = getDaysInMonth(currentMonth - 1, currentYear);
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth - 1, prevMonthDays - i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.toDateString() === today.toDateString()
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentYear, currentMonth, i);
      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.toDateString() === today.toDateString()
      });
    }

    // Next month days
    const totalCells = 42;
    const nextMonthDays = totalCells - days.length;
    for (let i = 1; i <= nextMonthDays; i++) {
      const date = new Date(currentYear, currentMonth + 1, i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: date.toDateString() === today.toDateString()
      });
    }

    return days;
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const isDateInRange = (date) => {
    if (!tempStartDate || !tempEndDate) return false;
    const dateStr = date.toISOString().split('T')[0];
    const start = new Date(tempStartDate);
    const end = new Date(tempEndDate);
    const current = new Date(dateStr);
    return current >= start && current <= end;
  };

  const isDateSelected = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return dateStr === tempStartDate || dateStr === tempEndDate;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const StatCard = ({ title, value, icon: Icon, color, onClick }) => (
    <Card
      sx={{
        height: '100%',
        transition: 'all 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: 'none',
        border: `1px solid ${colors.alpha.black08 || 'rgba(0,0,0,0.08)'}`,
      }}
      onClick={onClick}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: colors.text.primary,
                mb: 1,
                fontSize: isMobile ? '1.75rem' : '2.5rem'
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.text.secondary,
                fontWeight: 500
              }}
            >
              {title}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: `${color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon sx={{ fontSize: 32, color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const LineChart = ({ data, height = 300 }) => {
    const [chartWidth, setChartWidth] = React.useState(600);

    React.useEffect(() => {
      const updateWidth = () => {
        if (chartContainerRef.current) {
          setChartWidth(chartContainerRef.current.clientWidth);
        }
      };

      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }, [data]);

    if (!data || data.length === 0) return null;

    const chartHeight = isMobile ? 200 : isTablet ? 250 : height;
    const padding = {
      top: isMobile ? 30 : 40,
      right: isMobile ? 20 : 30,
      bottom: isMobile ? 40 : 50,
      left: isMobile ? 60 : 70
    };

    const innerWidth = chartWidth - padding.left - padding.right;
    const innerHeight = chartHeight - padding.top - padding.bottom;

    const maxAmount = Math.max(...data.map(d => d.amount), 1);
    const minAmount = Math.min(...data.map(d => d.amount), 0);
    const amountRange = maxAmount - minAmount;

    const getYScale = (value) => {
      return innerHeight - ((value - minAmount) / amountRange) * innerHeight;
    };

    const getXPosition = (index) => {
      return (index / (data.length - 1)) * innerWidth;
    };

    const generateYAxisLabels = () => {
      const labels = [];
      const steps = isMobile ? 3 : 5;

      for (let i = 0; i <= steps; i++) {
        const value = minAmount + (amountRange * i) / steps;
        labels.push({
          value: Math.round(value * 100) / 100,
          position: getYScale(value)
        });
      }
      return labels;
    };

    const createLinePath = () => {
      if (data.length === 0) return '';

      let path = `M ${padding.left} ${padding.top + getYScale(data[0].amount)}`;

      data.forEach((point, index) => {
        if (index === 0) return;
        const x = padding.left + getXPosition(index);
        const y = padding.top + getYScale(point.amount);
        path += ` L ${x} ${y}`;
      });

      return path;
    };

    const createAreaPath = () => {
      if (data.length === 0) return '';

      let path = `M ${padding.left} ${padding.top + getYScale(data[0].amount)}`;

      data.forEach((point, index) => {
        if (index === 0) return;
        const x = padding.left + getXPosition(index);
        const y = padding.top + getYScale(point.amount);
        path += ` L ${x} ${y}`;
      });

      path += ` L ${padding.left + innerWidth} ${padding.top + innerHeight}`;
      path += ` L ${padding.left} ${padding.top + innerHeight}`;
      path += ' Z';

      return path;
    };

    const yAxisLabels = generateYAxisLabels();

    return (
      <Box sx={{ position: 'relative', width: '100%', height: chartHeight }}>
        <svg
          width="100%"
          height={chartHeight}
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={colors.primary.main} />
              <stop offset="100%" stopColor={colors.primary.light} />
            </linearGradient>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={colors.primary.main} stopOpacity="0.3" />
              <stop offset="100%" stopColor={colors.primary.main} stopOpacity="0.1" />
            </linearGradient>
          </defs>

          {yAxisLabels.map((label, index) => (
            <line
              key={index}
              x1={padding.left}
              y1={padding.top + label.position}
              x2={padding.left + innerWidth}
              y2={padding.top + label.position}
              stroke={colors.alpha.black08}
              strokeWidth="1"
              strokeDasharray="2,2"
            />
          ))}

          <path
            d={createAreaPath()}
            fill="url(#areaGradient)"
            stroke="none"
          />

          <path
            d={createLinePath()}
            fill="none"
            stroke="url(#lineGradient)"
            strokeWidth={isMobile ? 2 : 3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {data.map((point, index) => {
            const x = padding.left + getXPosition(index);
            const y = padding.top + getYScale(point.amount);

            return (
              <g key={index}>
                <circle
                  cx={x}
                  cy={y}
                  r={isMobile ? 3 : 4}
                  fill={colors.primary.main}
                  stroke="#ffffff"
                  strokeWidth={2}
                />
                <circle
                  cx={x}
                  cy={y}
                  r={isMobile ? 8 : 10}
                  fill="transparent"
                  style={{ cursor: 'pointer' }}
                >
                  <title>
                    {formatCurrency(point.amount)} - {point.month}
                  </title>
                </circle>
              </g>
            );
          })}

          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={padding.top + innerHeight}
            stroke={colors.alpha.black50}
            strokeWidth="1"
          />

          <line
            x1={padding.left}
            y1={padding.top + innerHeight}
            x2={padding.left + innerWidth}
            y2={padding.top + innerHeight}
            stroke={colors.alpha.black50}
            strokeWidth="1"
          />

          {yAxisLabels.map((label, index) => (
            <text
              key={index}
              x={padding.left - (isMobile ? 5 : 10)}
              y={padding.top + label.position}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={isMobile ? 10 : 12}
              fill={colors.text.secondary}
              fontWeight="500"
            >
              {formatCurrency(label.value)}
            </text>
          ))}

          {data.map((point, index) => {
            const x = padding.left + getXPosition(index);
            return (
              <text
                key={index}
                x={x}
                y={padding.top + innerHeight + (isMobile ? 15 : 20)}
                textAnchor="middle"
                fontSize={isMobile ? 10 : 12}
                fill={colors.text.secondary}
                fontWeight="500"
              >
                {point.month.substring(0, 3)}
              </text>
            );
          })}
        </svg>

        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
          {data.map((point, index) => {
            const x = padding.left + getXPosition(index);
            const y = padding.top + getYScale(point.amount);

            return (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  left: `calc(${(x / chartWidth) * 100}% - ${isMobile ? 20 : 25}px)`,
                  top: `calc(${(y / chartHeight) * 100}% - ${isMobile ? 30 : 40}px)`,
                  bgcolor: colors.primary.main,
                  color: '#fff',
                  padding: '4px 8px',
                  borderRadius: 1,
                  fontSize: isMobile ? 10 : 12,
                  fontWeight: 600,
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  '&:hover': {
                    opacity: 1
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    border: '4px solid transparent',
                    borderTopColor: colors.primary.main
                  }
                }}
                className="chart-tooltip"
              >
                {formatCurrency(point.amount)}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <LoadingProgress loading={loading} />
      <Box sx={{ width: '100%' }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              '& .MuiAlert-message': {
                fontSize: '1rem'
              }
            }}
          >
            {error}
          </Alert>
        )}

        {!loading && !dashboardData && (
          <Alert severity="info">No dashboard data available</Alert>
        )}

        {!loading && dashboardData && (
          <>
            <PageBreadcrumbs
              items={[
                { label: 'Dashboard' }
              ]}
            />

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Total Projects"
                  value={dashboardData.projects || 0}
                  icon={WorkIcon}
                  color={colors.primary.main}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Total Users"
                  value={dashboardData.users || 0}
                  icon={PeopleIcon}
                  color={colors.success.main}
                  onClick={() => navigate('/customers')}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Active Subscriptions"
                  value={dashboardData.subscriptions || 0}
                  icon={SubscriptionsIcon}
                  color={colors.warning.main}
                  onClick={() => navigate('/subscriptions')}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard
                  title="Subscribed Users"
                  value={dashboardData.subscribed_users?.length || 0}
                  icon={PersonAddIcon}
                  color={colors.purple.main}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                mb: 3,
                gap: 2
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                    color: colors.text.primary
                  }}
                >
                  Cost Report
                </Typography>

                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  width: { xs: '100%', md: 'auto' },
                  alignItems: 'center'
                }}>
                  <Box
                    onClick={handleDatePickerOpen}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      border: `1px solid ${colors.border.main || '#e0e0e0'}`,
                      borderRadius: '8px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      minWidth: { xs: '100%', sm: 250 },
                      backgroundColor: colors.common.white,
                      '&:hover': {
                        borderColor: colors.primary.main,
                      }
                    }}
                  >
                    <CalendarTodayIcon sx={{ 
                      fontSize: 20, 
                      color: colors.primary.main 
                    }} />
                    <Typography variant="body2" sx={{
                      flex: 1,
                      fontSize: '0.8125rem',
                      color: colors.alpha.black70
                    }}>
                      {formatDate(startDate)} - {formatDate(endDate)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Date Range Picker Popover */}
              <Popover
                open={Boolean(datePickerAnchor)}
                anchorEl={datePickerAnchor}
                onClose={handleDatePickerClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    p: 2,
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    minWidth: 300,
                    maxWidth: 350
                  }
                }}
              >
                <Box>
                  {/* Calendar Header */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    px: 1
                  }}>
                    <IconButton 
                      size="small" 
                      onClick={() => navigateMonth('prev')}
                      sx={{ p: 0.5 }}
                    >
                      <Typography variant="body2">‹</Typography>
                    </IconButton>
                    
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {monthNames[currentMonth]} {currentYear}
                    </Typography>
                    
                    <IconButton 
                      size="small" 
                      onClick={() => navigateMonth('next')}
                      sx={{ p: 0.5 }}
                    >
                      <Typography variant="body2">›</Typography>
                    </IconButton>
                  </Box>

                  {/* Day Names */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 0.5,
                    mb: 1
                  }}>
                    {dayNames.map((day) => (
                      <Typography 
                        key={day}
                        variant="caption" 
                        align="center"
                        sx={{ 
                          fontWeight: 600,
                          color: colors.alpha.black60,
                          py: 0.5
                        }}
                      >
                        {day}
                      </Typography>
                    ))}
                  </Box>

                  {/* Calendar Days */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 0.5
                  }}>
                    {renderCalendar().map((day, index) => {
                      const dateStr = day.date.toISOString().split('T')[0];
                      const isSelected = isDateSelected(day.date);
                      const isInRange = isDateInRange(day.date);
                      const isToday = day.isToday;
                      const isCurrentMonth = day.isCurrentMonth;

                      return (
                        <Box
                          key={index}
                          onClick={() => handleDateSelect(day.date)}
                          sx={{
                            aspectRatio: '1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            position: 'relative',
                            backgroundColor: isSelected 
                              ? colors.primary.main 
                              : isInRange 
                                ? colors.primary.light 
                                : 'transparent',
                            color: isSelected 
                              ? colors.common.white 
                              : !isCurrentMonth 
                                ? colors.alpha.black30 
                                : colors.alpha.black70,
                            fontWeight: isSelected || isToday ? 600 : 400,
                            fontSize: '0.75rem',
                            '&:hover': {
                              backgroundColor: isSelected 
                                ? colors.primary.dark 
                                : colors.primary.lighter,
                              color: isSelected 
                                ? colors.common.white 
                                : colors.primary.main
                            },
                            ...(isToday && !isSelected && {
                              border: `1px solid ${colors.primary.main}`,
                              backgroundColor: 'transparent',
                              color: colors.primary.main
                            })
                          }}
                        >
                          {day.date.getDate()}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{
                    mt: 2,
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'flex-end'
                  }}>
                    <Button
                      size="small"
                      onClick={handleDatePickerClose}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem'
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApplyDateFilter}
                      disabled={!tempStartDate || !tempEndDate}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem'
                      }}
                    >
                      Apply Dates
                    </Button>
                  </Box>
                </Box>
              </Popover>

              <Box
                ref={chartContainerRef}
                sx={{
                  border: `1px solid ${colors.alpha.black08 || 'rgba(0,0,0,0.08)'}`,
                  borderRadius: 2,
                  bgcolor: colors.background.primary,
                  p: { xs: 2, sm: 3 },
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {costData.length === 0 ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 200
                  }}>
                    <Typography
                      variant="body2"
                      sx={{
                        opacity: 0.6,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        color: colors.text.secondary
                      }}
                    >
                      No cost data available for the selected period
                    </Typography>
                  </Box>
                ) : (
                  <LineChart data={costData} />
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default DashboardOverview;